import { FilterDate } from "./date.jsx";
import { VNodeChild } from "vue";
import {
    FilterStatic,
    FilterUsers,
} from "./base.jsx";
import {
    DateFilter,
    FilterOption,
    ListFilter,
    SelectedFilterValues,
    UsersFilter,
} from "./types.js";
import {
    loadMembersByIds,
    NamedEntity,
    searchByIdOrName,
    unserializeNamedEntities,
} from "./sources.js";
import { serializeIdSelection } from "./serialization.js";

function createDefaultSelection<TOption extends FilterOption>(): SelectedFilterValues<TOption> {
    return new Map();
}

function printSelection(values: SelectedFilterValues): VNodeChild {
    return values.size
        ? <span><b>{values.size}</b> séléctionné(s)</span>
        : "Aucun séléctionné";
}

function serializeSelection(values: SelectedFilterValues): string[] {
    return serializeIdSelection(values);
}

export function createEntityFilter({
    name,
    label,
    icon,
}: {
    name: string;
    label: string;
    icon: string;
}): UsersFilter {
    return {
        name,
        type        : "users",
        icon,
        label,
        component   : FilterUsers,
        default     : createDefaultSelection,
        selectOption(option) {
            return String(option.id);
        },
        printSelected: printSelection,
        unserialize: loadMembersByIds,
        serialize: serializeSelection,
    };
}

export function createStaticFilter<TOption extends NamedEntity>({
    name,
    label,
    icon,
    options,
    renderOption = (option) => option.name ?? option.id,
    searchOption = searchByIdOrName,
}: {
    name: string;
    label: string;
    icon: string;
    options: (() => Promise<TOption[]>) | TOption[];
    renderOption?: (option: TOption) => string;
    searchOption?: (options: TOption[], query: string) => TOption[];
}): ListFilter<TOption> {
    const loadOptions = typeof options === "function"
        ? options
        : async () => options;

    return {
        name,
        type        : "list",
        icon,
        label,
        component   : FilterStatic,
        default     : createDefaultSelection,
        printSelected: printSelection,
        async unserialize(values) {
            return unserializeNamedEntities(values, loadOptions);
        },
        serialize: serializeSelection,
        options,
        renderOption,
        selectOption(option) {
            return option.id;
        },
        searchOption,
    };
}

export function createDateRangeFilter({
    name,
    label,
    icon,
    start,
    end,
}: {
    name: string;
    label: string;
    icon: string;
    start: string;
    end: string;
}): DateFilter {
    return {
        name,
        icon,
        type        : "date",
        label,
        component   : FilterDate,
        default     : () => [],
        printSelected(values) {
            return values.length
                ? "Séléctionné !"
                : "Aucune séléctionnée";
        },
        async unserialize(values) {
            if (!Array.isArray(values))
                return [];

            return [ new Date(values[0]), new Date(values[1]) ];
        },
        serialize(values) {
            return [ values[0], values[1] ];
        },
        names: {
            start,
            end,
        },
    };
}
